import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/payroll/tax-brackets")({
  component: TaxBracketsPage,
});

import { useState, useMemo } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import PageHeader from "@/components/reusable-components/header/page-header";
import { TaxBracketTable } from "@/components/crud/table/TaxBracketTable";
import { TaxBracketFormSheet } from "@/components/crud/forms/TaxBracketFormSheet";

import useFetchTaxBrackets from "@/hooks/api/salaries/tax-brackets/useFetchTaxBrackets";
import useCreateTaxBracket from "@/hooks/api/salaries/tax-brackets/useCreateTaxBracket";
import useUpdateTaxBracket from "@/hooks/api/salaries/tax-brackets/useUpdateTaxBracket";
import useDeleteTaxBracket from "@/hooks/api/salaries/tax-brackets/useDeleteTaxBracket";

import type { TaxBracket, TaxBracketPayload } from "@/types/user/task-bracket";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function TaxBracketsPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "salary.setting.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const taxBracketsQuery = useFetchTaxBrackets({ page_size: pageSize, page });
  const taxBrackets = taxBracketsQuery.data?.results ?? [];
  const count = taxBracketsQuery.data?.count ?? 0;

  const createTaxBracket = useCreateTaxBracket();
  const updateTaxBracket = useUpdateTaxBracket();
  const deleteTaxBracket = useDeleteTaxBracket();

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const isMutating =
    createTaxBracket.isPending ||
    updateTaxBracket.isPending ||
    deleteTaxBracket.isPending;
  const isLoading = taxBracketsQuery.isPending || isMutating;
  const isFetching = taxBracketsQuery.isFetching || isMutating;

  // Sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingTaxBracket, setEditingTaxBracket] = useState<TaxBracket | null>(
    null
  );
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Delete state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [taxBracketToDelete, setTaxBracketToDelete] =
    useState<TaxBracket | null>(null);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handlers
  const handleCreate = () => {
    setEditingTaxBracket(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (taxBracket: TaxBracket) => {
    setEditingTaxBracket({ ...taxBracket });
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (taxBracket: TaxBracket) => {
    setEditingTaxBracket({ ...taxBracket });
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDelete = (taxBracket: TaxBracket) => {
    setTaxBracketToDelete(taxBracket);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taxBracketToDelete) return;
    try {
      await deleteTaxBracket.mutateAsync({ id: taxBracketToDelete.id });
      toast.success("Tax bracket deleted successfully");
      setIsDeleteDialogOpen(false);
      setTaxBracketToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: TaxBracketPayload) => {
    const payload: TaxBracketPayload = {
      ...data,
      rate: (parseFloat(data.rate) / 100).toString(),
    };
    if (editingTaxBracket) {
      await updateTaxBracket.mutateAsync({ id: editingTaxBracket.id, payload });
      toast.success("Tax bracket updated successfully");
    } else {
      await createTaxBracket.mutateAsync(payload);
      toast.success("Tax bracket created successfully");
    }
    setIsSheetOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Tax Brackets"
          description="Manage tax brackets used in payroll calculations."
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Tax Bracket"
          isLoading={isFetching}
        />

        <TaxBracketTable
          taxBrackets={taxBrackets}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <TaxBracketFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingTaxBracket={editingTaxBracket}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Tax Bracket"
          description={`Are you sure you want to delete the tax bracket with rate ${taxBracketToDelete ? (parseFloat(taxBracketToDelete.rate) * 100).toFixed(2) : ""}%? This action cannot be undone.`}
          itemName={`${taxBracketToDelete ? (parseFloat(taxBracketToDelete.rate) * 100).toFixed(2) : ""}%`}
          isLoading={deleteTaxBracket.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
